import { Select, Table, TableColumnsType } from "antd";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { getSurveyStudents } from "../../../api/surveyQuestion.api";
import useAxios from "../../../hooks/useAxios";
import config from "../../../lib/config";
import { IGetSurveyStudents, ISurveyResultParams, ISurveyStudent } from "../../../models/surveyQuestion.model";
import { RootState } from "../../../store/rootReducer";
import ErrorResponse from "../../ErrorResponse";
import Loader from "../../Loader";
import Pagination from "../../Pagination";
import SurveyStudentDetailModal from "./modals/SurveyStudentDetailModal";
import ResultSearchBar from "./ResultSearchBar";

interface IProps {
  surveyCode: number|undefined;
}

const SurveyResult4: FC<IProps> = ({
  surveyCode,
}) => {
  const { t } = useTranslation();
  const { auth, currentCampus } = useSelector((state: RootState) => state.auth);
  const { data: res, isLoading, error, execute } = useAxios<IGetSurveyStudents>();

  const [pageNo, setPageNo] = useState(1);
  const [completeYN, setCompleteYN] = useState('');
  const [campusCode, setCampusCode] = useState('');
  const [courseCode, setCourseCode] = useState('');
  const [surveyStudentDetailVisible, setSurveyStudentDetailVisible] = useState(false);
  const [record, setRecord] = useState<ISurveyStudent>();

  useEffect(() => {
    if (surveyCode) {
      doGetSurveyStudents();
    }
  }, [surveyCode, pageNo]);

  const doGetSurveyStudents = (_pageNo?: number) => {
    if (!surveyCode) return;

    const req: ISurveyResultParams = {
      pageNo: _pageNo || pageNo,
      perPage: config.miniPageSize,
    };

    if (completeYN) req.completeYN = completeYN;
    if (auth.hqYn === 'Y') {
      if (campusCode) req.campusCode = campusCode;
    } else {
      req.campusCode = currentCampus;
    }
    if (courseCode) req.courseCode = courseCode;

    execute(getSurveyStudents(surveyCode, req));
  }

  const onSearch = () => {
    doGetSurveyStudents(1);
  }

  const columns: TableColumnsType<ISurveyStudent> = [
    {
      title: t('common.menu.campus'),
      dataIndex: 'campusName',
      key: 'campusName',
      align: 'center',
    },
    {
      title: t('homepage.archive.column4'),
      dataIndex: 'courseName',
      key: 'courseName',
      align: 'center',
    },
    {
      title: t('common.menu.class'),
      dataIndex: 'className',
      key: 'className',
      align: 'center',
    },
    {
      title: t('event.education.column22'),
      dataIndex: 'memberName',
      key: 'memberName',
      align: 'center',
    },
    {
      title: t('homepage.survey.column8'),
      dataIndex: 'score',
      key: 'score',
      align: 'center',
      render: value => value ? value.toFixed(1) : 'N/A',
    },
    {
      title: t('homepage.survey.column10'),
      dataIndex: 'completeYN',
      key: 'completeYN',
      align: 'center',
    },
  ];

  return (
    <>
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />

      <div className="comm-bx__between mb-0">
        <div className="comm-sch_result">{t('archive.exam.text1')}: <b>{res?.total || 0}</b></div>
        <div className="flex">
          <div className="v-line mr-1">{t('homepage.education.column1')}</div>
          <div className="w-150p mr-2">
            <Select
              className="w-100"
              value={completeYN}
              onChange={value => setCompleteYN(value)}
            >
              <Select.Option value="">{t('common.text.all')}</Select.Option>
              <Select.Option value="Y">{t('homepage.survey.option1')}</Select.Option>
              <Select.Option value="N">{t('homepage.survey.option2')}</Select.Option>
            </Select>
          </div>
          <ResultSearchBar
            campusCode={campusCode}
            setCampusCode={setCampusCode}
            courseCode={courseCode}
            setCourseCode={setCourseCode}
            onSearch={onSearch}
          />
        </div>
      </div>
      <div className="comm-table__area">
        <Table
          columns={columns}
          dataSource={res?.data}
          pagination={{
            current: pageNo,
            pageSize: config.miniPageSize,
          }}
          onRow={(record) => {
            return {
              onClick: () => {
                if (record.completeYN === 'Y') {
                  setRecord(record);
                  setSurveyStudentDetailVisible(true);  
                }
              },
            };
          }}
          rowClassName={record => record.completeYN === 'Y' ? 'pointer' : ''}
        />
      </div>
      <div className="comm-bx__between">
        <div className="common-paging center">
          <Pagination
            count={res?.total || 0}
            page={pageNo}
            setPage={setPageNo}
            mini
          />
        </div>
      </div>

      {surveyStudentDetailVisible &&
        <SurveyStudentDetailModal
          visible={surveyStudentDetailVisible}
          setVisible={setSurveyStudentDetailVisible}
          surveyCode={surveyCode}
          record={record}
        />
      }
    </>
  );
}

export default SurveyResult4;
